export const scheduleEventListRefreshTop = ({
  isSearchMode = false,
  orderFrozen = false,
  isBootLoading = false,
  refreshTopTimerRef,
  fetchEvents,
  delayMs = 120,
} = {}) => {
  if (isSearchMode || orderFrozen || isBootLoading) return false;
  if (!refreshTopTimerRef || typeof fetchEvents !== 'function') return false;

  if (refreshTopTimerRef.current) clearTimeout(refreshTopTimerRef.current);
  refreshTopTimerRef.current = setTimeout(() => {
    Promise.resolve(fetchEvents('refreshTop')).finally(() => {
      refreshTopTimerRef.current = null;
    });
  }, Number.isFinite(Number(delayMs)) ? Number(delayMs) : 120);

  return true;
};

const SCROLLABLE_OVERFLOW_PATTERN = /(auto|scroll|overlay)/i;

const isScrollableElement = (node) => {
  if (!node || typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') {
    return false;
  }
  const style = window.getComputedStyle(node);
  const overflowY = String(style?.overflowY || style?.overflow || '').toLowerCase();
  return SCROLLABLE_OVERFLOW_PATTERN.test(overflowY);
};

export const resolveEventListScrollContainer = (container) => {
  if (!container || typeof window === 'undefined') return container || null;
  if (isScrollableElement(container)) return container;

  let current = container.parentElement || null;
  while (current && current !== document.body) {
    if (isScrollableElement(current)) return current;
    current = current.parentElement || null;
  }

  return container;
};

export const shouldLoadMoreEventList = ({
  isSearchMode = false,
  isBootLoading = false,
  isLoadingEvents = false,
  hasMoreEvents = false,
} = {}) => !isSearchMode && !isBootLoading && !isLoadingEvents && hasMoreEvents;

export const loadMoreEventList = async ({
  canLoadMore = true,
  fetchEvents,
  pageLimit = 20,
  setHasMoreEvents,
} = {}) => {
  if (!canLoadMore || typeof fetchEvents !== 'function') return 0;

  const loadedCount = Number(await fetchEvents('append')) || 0;
  if (loadedCount < Number(pageLimit || 0) && typeof setHasMoreEvents === 'function') {
    setHasMoreEvents(false);
  }
  return loadedCount;
};

export const observeEventListLoadMoreSentinel = ({
  sentinel,
  root = null,
  shouldLoad,
  onLoadMore,
  rootMargin = '200px 0px',
  threshold = 0,
} = {}) => {
  if (!sentinel) return () => {};
  if (typeof IntersectionObserver === 'undefined') return () => {};
  if (typeof onLoadMore !== 'function') return () => {};

  const shouldLoadResolver = typeof shouldLoad === 'function' ? shouldLoad : () => true;
  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries?.[0];
      if (!entry?.isIntersecting) return;
      if (!shouldLoadResolver()) return;
      onLoadMore();
    },
    { root, rootMargin, threshold }
  );
  observer.observe(sentinel);
  return () => observer.disconnect();
};

export const observeEventListLoadMoreScroll = ({
  container,
  shouldLoad,
  onLoadMore,
  thresholdPx = 160,
} = {}) => {
  const scrollContainer = resolveEventListScrollContainer(container);
  if (!scrollContainer) return () => {};
  if (typeof onLoadMore !== 'function') return () => {};

  const maybeLoad = () =>
    triggerEventListLoadMoreIfNeeded({
      container: scrollContainer,
      shouldLoad,
      onLoadMore,
      thresholdPx,
    });

  const handleScroll = () => {
    maybeLoad();
  };

  scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
  const rafId =
    typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame(maybeLoad)
      : null;

  return () => {
    scrollContainer.removeEventListener('scroll', handleScroll);
    if (rafId != null && typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
      window.cancelAnimationFrame(rafId);
    }
  };
};

export const triggerEventListLoadMoreIfNeeded = ({
  container,
  shouldLoad,
  onLoadMore,
  thresholdPx = 160,
} = {}) => {
  const scrollContainer = resolveEventListScrollContainer(container);
  if (!scrollContainer) return false;
  if (typeof onLoadMore !== 'function') return false;

  const shouldLoadResolver = typeof shouldLoad === 'function' ? shouldLoad : () => true;
  const threshold = Number.isFinite(Number(thresholdPx)) ? Number(thresholdPx) : 160;
  const remaining =
    scrollContainer.scrollHeight - scrollContainer.clientHeight - scrollContainer.scrollTop;
  const canLoad = shouldLoadResolver();
  if (!canLoad) return false;

  if (remaining > threshold) {
    return false;
  }

  onLoadMore();
  return true;
};
